import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo } from "react";
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { FeatureFlag, Tenant, queries } from "@oneleet/api_client";
import { OneleetRole, TenantRole } from "@oneleet/api_client";
import { ONELEET_ADMIN_ROLES } from "@oneleet/config/constants";
import {
  DEFAULT_ANIMATION_DURATION_MILLISECONDS,
  MILLISECONDS_IN_A_SECOND,
} from "@oneleet/config/constants";
import {
  OutletContext,
  clearUser,
  identifyUser,
  printOneleetConsoleBlurb,
} from "@oneleet/utils";
import { NotificationProvider } from "@oneleet/utils/src/hooks/useNotify";

import { MotionConfig } from "framer-motion";

import {
  ErrorBoundary as ErrorBoundaryLayout,
  Loader,
} from "@oneleet/components";
import { PostHogProvider } from "@oneleet/components";
import GlobalErrorPopout from "@oneleet/components/src/notifications/globalerror";

printOneleetConsoleBlurb();

const APP_ID = "app";

const AUTHORIZED_ROLES = [
  OneleetRole.CLIENT,
  ...ONELEET_ADMIN_ROLES,
  OneleetRole.PENTESTER_ADMIN,
];

export default function App() {
  const { tenant: tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const metadataQuery = useQuery(queries.meta.get);
  const currentUserQuery = useQuery({
    ...queries.user.current,
    retry: (failureCount, error) => {
      // Don't retry if it's a 401 error, this means the user is not logged in
      if (error instanceof AxiosError && error.status === 401) {
        return false;
      }

      // Default retry behavior for other errors
      return failureCount < 3;
    },
  });

  const user = currentUserQuery.data;
  const currentUserQueryFailedWith401 =
    currentUserQuery.isError &&
    currentUserQuery.error instanceof AxiosError &&
    currentUserQuery.error.status === 401;

  const invitesQuery = useQuery({
    ...queries.user.invites,
    enabled: !!user && AUTHORIZED_ROLES.includes(user.oneleetRole),
  });

  const membershipsQuery = useQuery({
    ...queries.membership.list,
    enabled: !!user && AUTHORIZED_ROLES.includes(user.oneleetRole),
  });

  const featureFlagsQuery = useQuery({
    ...queries.tenantFeatureFlag.list(tenantId),
    enabled: !!user && !!tenantId,
  });

  const invites = useMemo(() => {
    return invitesQuery.data ?? [];
  }, [invitesQuery.data]);

  const memberships = useMemo(() => {
    return membershipsQuery.data ?? [];
  }, [membershipsQuery.data]);

  const currentMembership = useMemo(() => {
    return memberships.find((membership) => membership.tenantId === tenantId);
  }, [memberships, tenantId]);

  const featureFlags = useMemo(() => {
    const flags = featureFlagsQuery.data ?? [];
    return flags.reduce(
      (accumulator, flag) => {
        accumulator[flag.id] = flag;
        return accumulator;
      },
      {} as Record<string, FeatureFlag>,
    );
  }, [featureFlagsQuery.data]);

  const hasFeatureFlag = useCallback(
    (flag: string) => {
      return featureFlags[flag] !== undefined;
    },
    [featureFlags],
  );

  const isOneleetAdmin = useMemo(() => {
    return user !== undefined && ONELEET_ADMIN_ROLES.includes(user.oneleetRole);
  }, [user]);
  const isAdmin = useMemo(() => {
    return currentMembership?.role === TenantRole.ADMIN || isOneleetAdmin;
  }, [currentMembership, isOneleetAdmin]);

  const allTenantsQuery = useQuery({
    ...queries.tenant.list,
    enabled: isOneleetAdmin,
  });

  const allTenants = useMemo(() => {
    return allTenantsQuery.data ?? [];
  }, [allTenantsQuery.data]);

  const tenants = useMemo(() => {
    const uniqueTenants: Tenant[] = [];
    const seenTenants = new Set<string>();

    for (const tenant of memberships.map((m) => m.tenant)) {
      if (tenant && !seenTenants.has(tenant.id)) {
        uniqueTenants.push(tenant);
        seenTenants.add(tenant.id);
      }
    }

    for (const tenant of allTenants) {
      if (tenant && !seenTenants.has(tenant.id)) {
        uniqueTenants.push(tenant);
        seenTenants.add(tenant.id);
      }
    }

    return uniqueTenants;
  }, [memberships, allTenants]);

  const tenant = useMemo(() => {
    return tenants.find((tenant) => tenant.id === tenantId);
  }, [tenantId, tenants]);

  // Handle redirects based on auth status
  useEffect(() => {
    let redirectPath: string | null = null;

    // The first if statement should only check for currentUserQueryFailedWith401 here, not if the user object exists,
    // as it's possible that the user object exists in the cache, but the query is now failing with a 401
    // This is a common case when a user logs out in one tab, and then switches over to another tab.
    if (currentUserQueryFailedWith401) {
      // Case 1: No user found - redirect to login
      clearUser();

      // Clear the user from query cache to ensure it doesn't persist after 401, which would lead to an infinite redirect loop
      // This is a common case when a user logs out in one tab, and then switches over to another tab.
      queryClient.removeQueries({ queryKey: queries.user.current.queryKey });

      // Cancel all other inflight queries to avoid issues with flashes from re-queries and 401 errors on other queries persisting in the cache
      // This is a common case when a user logs out in one tab, and then switches over to another tab.
      // Important note: the meta query must not be cancelled, otherwise a race condition can occur in staging/production that does not appear in dev where the meta query gets cancelled and stuck in a pending state, causing the app to not be able to render the login page
      void queryClient.cancelQueries({
        predicate: (query) => query.queryKey !== queries.meta.get.queryKey,
      });

      // Capture next URL for redirect after login
      const next = location.pathname + location.search;
      const nextUrlEncoded = encodeURIComponent(next);
      redirectPath =
        next === "/" ? "/auth/login" : `/auth/login?next=${nextUrlEncoded}`;
    } else if (user) {
      identifyUser(user);

      // Check if user is authorized
      if (!AUTHORIZED_ROLES.includes(user.oneleetRole)) {
        // Case 2: User lacks required role - redirect to unauthorized
        redirectPath = "/unauthorized";
      } else if (invites.length > 0) {
        // Case 3: User has invites - redirect to invites
        redirectPath = "/invites";
      } else if (membershipsQuery.isSuccess) {
        if (ONELEET_ADMIN_ROLES.includes(user.oneleetRole)) {
          // Handle oneleet admin users
          if (!tenantId && tenants.length > 0) {
            redirectPath = `/tenants/${tenants[0].id}`;
          }
        } else {
          // Handle client users
          if (memberships.length === 0) {
            // Case 4: User has no memberships - redirect to unauthorized
            redirectPath = "/unauthorized";
          } else if (!tenantId) {
            // Case 5: No tenant specified - redirect to first tenant
            redirectPath = `/tenants/${memberships[0].tenantId}`;
          }
        }
      }
    }

    if (redirectPath) {
      // Extract just the path portion without query parameters. Avoids infinite redirects due to query params like next=
      const redirectPathWithoutQuery = redirectPath.split("?")[0];

      // Only redirect if the current path is different from the redirect path
      if (!location.pathname.startsWith(redirectPathWithoutQuery)) {
        navigate(redirectPath);
      }
    }
  }, [
    user,
    currentUserQueryFailedWith401,
    memberships,
    tenants,
    tenantId,
    invites.length,
    location.pathname,
    location.search,
    membershipsQuery.isSuccess,
    currentUserQuery.isError,
    currentUserQuery.error,
    navigate,
    queryClient,
  ]);

  // We have to check isLoading for all queries besides the metadata and currentUser
  // because they may be disabled based on the results of the user query
  if (
    metadataQuery.isPending ||
    currentUserQuery.isPending ||
    invitesQuery.isLoading ||
    membershipsQuery.isLoading ||
    allTenantsQuery.isLoading ||
    featureFlagsQuery.isLoading
  ) {
    return <Loader delayed />;
  }

  if (
    metadataQuery.isError ||
    ((currentUserQuery.isError ||
      invitesQuery.isError ||
      membershipsQuery.isError ||
      allTenantsQuery.isError ||
      featureFlagsQuery.isError) &&
      !currentUserQueryFailedWith401)
  ) {
    return (
      <GlobalErrorPopout
        title="Something went wrong"
        description="Failed to load the page. Please try again later."
      />
    );
  }

  const metadata = metadataQuery.data;

  return (
    <PostHogProvider
      apiKey={metadata.env.POSTHOG_PUBLIC_API_KEY}
      apiHost={`${import.meta.env.VITE_API_URL}/posthog`}
      tenant={currentMembership?.tenant?.name ?? "Unknown tenant"}
      user={user ?? null}
      settings={{
        disable_session_recording: import.meta.env.MODE !== "production",
      }}
      location={location.pathname}
    >
      <MotionConfig
        transition={{
          type: "tween",
          duration:
            DEFAULT_ANIMATION_DURATION_MILLISECONDS / MILLISECONDS_IN_A_SECOND,
        }}
      >
        <NotificationProvider>
          <Outlet
            context={
              {
                appId: APP_ID,
                metadata,
                user,
                tenant,
                tenants,
                memberships,
                currentMembership,
                invites,
                isAdmin,
                isOneleetAdmin,
                hasFeatureFlag,
              } as OutletContext
            }
          />
          {metadata.env.IS_DEV_ENVIRONMENT && <ReactQueryDevtools />}
          <ScrollRestoration />
          <script
            dangerouslySetInnerHTML={{
              __html: `window.env = ${JSON.stringify(metadata.env || {})}`,
            }}
          />
        </NotificationProvider>
      </MotionConfig>
    </PostHogProvider>
  );
}

export function ErrorBoundary() {
  return <ErrorBoundaryLayout />;
}
